import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { UdzurMasuk, UDZUR_LABEL } from '../../../utils/presensiLogic';

interface PortalGuruModalPresensiTerlambatProps {
    onClose: () => void;
    onSubmit: (reason: string, udzur?: UdzurMasuk) => void;
    loading?: boolean;
    /**
     * Masa penyesuaian: pilihan "Lupa" dan "Aplikasi Error" ditampilkan.
     * `jamToleransi` = jam yang dicatat bila salah satunya dipilih (mis. "07:00").
     * `dalamBatas` = false bila terlambat 2 jam atau lebih (pilihan tampil tapi nonaktif).
     */
    udzur?: { jamToleransi: string; dalamBatas: boolean } | null;
}

type Pilihan = 'alasan' | 'tanpa' | UdzurMasuk;

export const PortalGuruModalPresensiTerlambat: React.FC<PortalGuruModalPresensiTerlambatProps> = ({ onClose, onSubmit, loading, udzur }) => {
    const [reason, setReason] = useState('');
    const [pilihan, setPilihan] = useState<Pilihan>('alasan');

    const isUdzur = pilihan === 'lupa' || pilihan === 'aplikasi_error';

    const handleSubmit = () => {
        if (pilihan === 'tanpa') onSubmit('Tidak ada alasan');
        else if (isUdzur) onSubmit(UDZUR_LABEL[pilihan as UdzurMasuk], pilihan as UdzurMasuk);
        else if (reason.trim()) onSubmit(reason);
    };

    const isSubmitDisabled = pilihan === 'alasan' && !reason.trim();

    // Klik pilihan yang sedang terpilih membatalkannya (kembali ke isi alasan).
    const pilih = (p: Pilihan) => setPilihan(cur => (cur === p ? 'alasan' : p));

    const opsi: { id: Pilihan; label: string; nonaktif?: boolean }[] = [
        { id: 'tanpa', label: 'Tidak ada alasan' },
        ...(udzur ? [
            { id: 'lupa' as Pilihan, label: 'Lupa presensi', nonaktif: !udzur.dalamBatas },
            { id: 'aplikasi_error' as Pilihan, label: 'Aplikasi error', nonaktif: !udzur.dalamBatas },
        ] : []),
    ];

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
            <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"></div>
            <div className="relative w-full max-w-sm max-h-[92vh] overflow-y-auto bg-white rounded-3xl p-6 shadow-2xl ring-1 ring-white/10 transform transition-all animate-in slide-in-from-bottom-10 fade-in duration-300 text-center">

                <div className="mx-auto w-24 h-24 rounded-full bg-amber-100 flex items-center justify-center mb-4 ring-8 ring-amber-50">
                    <AlertTriangle size={48} className="text-amber-500" />
                </div>

                <h3 className="text-xl font-bold text-[#111813] tracking-tight mb-2">Presensi Terlambat</h3>
                <p className="text-sm text-[#63886f] mb-6 leading-relaxed">
                    Afwan, Anda melewati batas waktu masuk hari ini. Mari kita niatkan kembali untuk selalu istiqomah dalam kedisiplinan demi keberkahan khidmah kita.
                </p>

                <div className="text-left mb-6">
                    <label className="block text-sm font-semibold text-[#111813] mb-2" htmlFor="reason">Alasan Terlambat</label>
                    <textarea
                        className="w-full rounded-xl border-gray-200 bg-gray-50 text-[#111813] focus:border-amber-500 focus:ring-amber-500 text-sm p-3 min-h-[100px] resize-none outline-none ring-1 ring-transparent transition-all disabled:opacity-50"
                        id="reason"
                        placeholder="Tuliskan alasan keterlambatan Anda di sini..."
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        disabled={pilihan !== 'alasan' || loading}
                    />
                    <div className="mt-3 space-y-2">
                        {opsi.map(o => (
                            <div key={o.id} className="flex items-center">
                                <input
                                    className="h-4 w-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500 cursor-pointer disabled:cursor-not-allowed"
                                    id={`terlambat-${o.id}`}
                                    type="checkbox"
                                    checked={pilihan === o.id}
                                    onChange={() => pilih(o.id)}
                                    disabled={loading || o.nonaktif}
                                />
                                <label
                                    className={`ml-2 text-sm cursor-pointer select-none ${o.nonaktif ? 'text-gray-400' : 'text-[#63886f]'}`}
                                    htmlFor={`terlambat-${o.id}`}
                                >
                                    {o.label}
                                </label>
                            </div>
                        ))}
                    </div>

                    {udzur && isUdzur && (
                        <p className="mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-xs leading-relaxed text-emerald-800">
                            Masa penyesuaian aplikasi: presensi Anda dicatat <b>tepat waktu pukul {udzur.jamToleransi}</b> sebagai udzur.
                        </p>
                    )}
                    {udzur && !udzur.dalamBatas && (
                        <p className="mt-3 text-xs leading-relaxed text-gray-500">
                            Pilihan Lupa dan Aplikasi error hanya berlaku bila terlambat kurang dari 2 jam.
                        </p>
                    )}
                </div>

                <button
                    onClick={handleSubmit}
                    disabled={isSubmitDisabled || loading}
                    className={`w-full py-3.5 font-bold rounded-xl shadow-lg transition-all active:scale-[0.98] ${
                        isSubmitDisabled || loading
                            ? 'bg-gray-200 text-gray-400 shadow-none cursor-not-allowed'
                            : isUdzur
                                ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/25'
                                : 'bg-amber-500 hover:bg-amber-600 text-white shadow-amber-500/25'
                    }`}
                >
                    {loading ? 'Menyimpan...' : isUdzur ? 'Simpan Presensi' : 'Kirim Alasan & Simpan'}
                </button>
            </div>
        </div>
    );
};
